// Недельный отчет: подписи, даты и картинки для views/WeekReport.vue,
// components/report/* и карточки на вкладке «Неделя». Числа считает сервер
// (lib/weeklyReport.js), здесь только форма показа.
import { imgUrl } from './image.js'

const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
const WEEKDAYS_LONG = { 'пн': 'понедельник', 'вт': 'вторник', 'ср': 'среда', 'чт': 'четверг', 'пт': 'пятница', 'сб': 'суббота', 'вс': 'воскресенье' }

export const COLORS = {
  kcal: '#EE998C', protein: '#9FC2C1', fat: '#EBC06D', sat: '#D7AA49', carbs: '#EEAD77',
  sugar: '#DE8F6A', fiber: '#A6B779', veg: '#A6B779', water: '#ABDEEB', weight: '#B98A5E',
  band: 'rgba(95, 143, 78, 0.16)', bandLine: 'rgba(95, 143, 78, 0.55)', limit: '#B98A5E',
}

export const PLATE_PARTS = [
  { key: 'veg_fruit', label: 'Овощи, фрукты, ягоды', color: '#A6B779' },
  { key: 'protein', label: 'Источники белка', color: '#9FC2C1' },
  { key: 'carbs', label: 'Сложные углеводы', color: '#EEAD77' },
  { key: 'sweets', label: 'Сладкое', color: '#EE998C' },
]

// Ответ для карточки на вкладке «Неделя» живёт между её показами: вкладку
// листают туда-обратно, а отчет за прошлую неделю за минуты не меняется
export const cardCache = { at: 0, resp: null }

export const MEAL_LABELS = { breakfast: 'Завтрак', lunch: 'Обед', dinner: 'Ужин', snack: 'Перекусы' }

const ymd = (s) => { const [y, m, d] = String(s).split('-').map(Number); return { y, m, d } }

/** «5–11 октября», «28 сентября – 4 октября», «29 декабря 2025 – 4 января 2026» */
export function weekRange(start, end) {
  if (!start || !end) return ''
  const a = ymd(start), b = ymd(end)
  if (a.y !== b.y) return `${a.d} ${MONTHS_GEN[a.m - 1]} ${a.y} – ${b.d} ${MONTHS_GEN[b.m - 1]} ${b.y}`
  if (a.m !== b.m) return `${a.d} ${MONTHS_GEN[a.m - 1]} – ${b.d} ${MONTHS_GEN[b.m - 1]}`
  return `${a.d}–${b.d} ${MONTHS_GEN[a.m - 1]}`
}

export function dayLabel(date, days) {
  const d = (days || []).find(x => x.date === date)
  if (d) return WEEKDAYS_LONG[d.weekday] || d.weekday
  const { d: n, m } = ymd(date)
  return `${n} ${MONTHS_GEN[m - 1]}`
}

export function plural(n, forms) {
  const a = Math.abs(n) % 100, b = a % 10
  if (a > 10 && a < 20) return forms[2]
  if (b > 1 && b < 5) return forms[1]
  if (b === 1) return forms[0]
  return forms[2]
}

/** Число по-русски: запятая, без хвостовых нулей */
export function num(v, decimals = 0) {
  if (v == null || !Number.isFinite(Number(v))) return '—'
  const k = Math.pow(10, decimals)
  const r = Math.round(Number(v) * k) / k
  return String(r).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

export function times(n) {
  return `${n} ${plural(n, ['раз', 'раза', 'раз'])}`
}

/** Снимок блюда, иначе иконка продукта, иначе ничего */
export function pictureOf(item) {
  if (item?.photo_url) return { src: imgUrl(item.photo_url), kind: 'photo' }
  if (item?.icon_key) return { src: imgUrl(`/food-icons/${item.icon_key}.webp`), kind: 'icon' }
  return null
}

export function userTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Moscow' } catch { return 'Europe/Moscow' }
}

export const DENSITY = {
  dense: { label: 'чистый белок', hint: 'от 10 г на 100 ккал' },
  mid: { label: 'обычный', hint: '6–10 г на 100 ккал' },
  loaded: { label: 'с довеском', hint: 'белок вместе с жирами или углеводами' },
}

export const GOAL_DIRECTION_LABELS = { lose: 'снижение веса', gain: 'набор массы', recomp: 'рекомпозиция', maintain: 'поддержание' }

/** Разделы отчета, которые показываем: есть данные и человек их не скрыл */
export function visibleSections(data, hidden = []) {
  const h = new Set(hidden)
  return {
    hero: !!data?.hero,
    goal: !!data?.goal,
    plate: !!data?.plate,
    rhythm: (data?.rhythm?.meals?.length || 0) > 0,
    energy: !h.has('energy') && !!data?.norms,
    protein: !h.has('protein') && !!data?.protein,
    fats: !h.has('fats') && !!data?.fats,
    carbs: !h.has('carbs') && !!data?.carbs,
    sugar: !h.has('sugar') && !!data?.carbs?.sugar,
    fiber: !h.has('fiber') && !!data?.carbs?.fiber,
    vitamins: !h.has('vitamins') && (data?.vitamins?.rows?.length || 0) > 0,
    weight: !h.has('weight') && !!data?.goal?.weight,
    hunger: !h.has('hunger') && data?.modules?.hunger?.enabled !== false && data?.modules?.hunger?.enough === true,
  }
}

/** Раздел идёт без цифр и графиков (у «срывающихся»: энергия, белок, жиры, углеводы) */
export function showsNumbers(data, key) {
  return !(data?.text_only || []).includes(key)
}
